import React, { Component } from "react";

import { getRequest } from "../../api/ApiService";
import PeerEvaluationModal from "../peer-evaluation/peer-evaluation-modal";
import PeerEvaluationFeedback from "../peer-evaluation/peer-evaluation-feedback";
import Auth from "../../routes/Auth";
import sortBy from "lodash/sortBy";
import styled from "styled-components";

const ChartButton = styled.div`
  font-family: Lato;
  font-size: 16px;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0 10px;

  &:last-child {
    justify-content: flex-end;
  }
  &:first-child {
    justify-content: flex-start;
  }
`;

const ChartButtonContent = styled.div`
  display: inline-block;
  text-align: center;

  h3 {
    font-weight: bold;
    font-size: 16px;
    margin-bottom: 5px;
    color: #36507e;
  }

  span {
    color: #74abec;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ucFirst = str => (str ? str[0].toUpperCase() + str.slice(1) : str);

class ChartViewMore extends Component {
  constructor(props) {
    super(props);

    this._isMounted = false;

    const userId = parseInt(Auth.getUserId(), 10);

    this.state = {
      currentUserId: userId,
      currentTeamId: Auth.getTeamId(),
      readonlyMode: true,
      questionsOrder: [],
      isIntroScreen: true,
      isFinalScreen: false,
      isTransitionScreen: false,
      currentPage: 0,
      questionsCount: 0,
      peer_rating_count: 0,
      show: false,
      progressBar: [],
      teamList: [],
      submitPages: {},
    };
  }

  async componentDidMount() {
    this._isMounted = true;

    await this.getTeamList();

    let progressBar = [
      {
        id: 1,
        title: "Peer Rating",
        progress: 0
      }
    ];

    if (!this._isMounted) return;

    this.setState({
      progressBar
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  async getTeamList() {
    const { currentUserId, currentTeamId } = this.state;
    const { body: users } = await getRequest(
      `${process.env.REACT_APP_API_URL}team/players/?team=${currentTeamId}`
    );

    const teamList = sortBy(users.filter(
      teammate => teammate.role !== 'VI'
        && teammate.team === currentTeamId
        && teammate.user
        && teammate.user.id !== currentUserId
    ).map(teammate => teammate.user), "id")
      .map(teammate => {
        return {
          id: teammate.id,
          username: teammate.username,
          fullname: `${teammate.first_name} ${teammate.last_name}`
        };
      });

    if (!this._isMounted) return;

    this.setState({
      teamList,
    }, () => {
      this.questionsOrder();
    });
  }

  questionsOrder = () => {
    const { survey, reports } = this.props;
    const questions = reports[survey.stage] ? reports[survey.stage].user_score_question : [];

    const peer_rating_questions = questions.map((item, index) => (
      {
        id: index + 1,
        text: item.question,
        category: 'likert',
        answer_type: 'rating',
        value: Math.round(item.median)
      }
    ));

    let questionsOrder = [
      { answer_type: "peer_rating_feedback_intro" },
      ...peer_rating_questions
    ];

    questionsOrder.push({ answer_type: "final_peer_rating_page" });

    this.setState(() => ({
      questionsOrder,
      questionsCount: questionsOrder.length - 1,
      peer_rating_count: peer_rating_questions.length,
    }));
  };

  isIntroScreen = nextPage => {
    const { questionsOrder } = this.state;
    const type = questionsOrder[nextPage].answer_type;

    return (
      type === "peer_rating_feedback_intro"
    );
  };

  isFinalScreen = nextPage => {
    const { questionsOrder } = this.state;
    const type = questionsOrder[nextPage].answer_type;

    return type === "final_peer_rating_page";
  }

  isTransitionScreen = nextPage => {
    const { questionsOrder } = this.state;
    const type = questionsOrder[nextPage].answer_type;

    return type === "transition_page";
  }

  handleShowModal = () => {
    this.setState(
      () => ({ show: true }),
    );
  };

  handleHideModal = () => {
    this.setState(prevState => ({
      show: false,
      currentPage: 0,
      isIntroScreen: true,
      isFinalScreen: false,
      isTransitionScreen: false,
      submitPages: {},
      progressBar: prevState.progressBar.map(item => ({ ...item, progress: 0 }))
    }));
  };

  goToNextPage = () => {
    const {
      currentPage,
      questionsCount,
      peer_rating_count
    } = this.state;

    let nextPage = currentPage + 1;
    if (nextPage > questionsCount) {
      nextPage = questionsCount;
    }

    const isIntroScreen = this.isIntroScreen(nextPage);
    const isFinalScreen = this.isFinalScreen(nextPage);
    const isTransitionScreen = this.isTransitionScreen(nextPage);

    this.setState(prevState => ({
      currentPage: nextPage,
      isIntroScreen,
      isFinalScreen,
      isTransitionScreen,
      progressBar: prevState.progressBar.map(item => (
        {
          ...item,
          progress: Math.min((nextPage / peer_rating_count) * 100, 100),
        }
      ))
    }));
  };

  goToPrevPage = () => {
    const {
      currentPage,
      peer_rating_count
    } = this.state;

    let prevPage = currentPage - 1;
    if (prevPage < 1) {
      prevPage = 1;
    }

    const isIntroScreen = this.isIntroScreen(prevPage);
    const isFinalScreen = this.isFinalScreen(prevPage);
    const isTransitionScreen = this.isTransitionScreen(prevPage);

    this.setState(prevState => ({
      currentPage: prevPage,
      isIntroScreen,
      isFinalScreen,
      isTransitionScreen,
      progressBar: prevState.progressBar.map(item => (
        {
          ...item,
          progress: Math.min((prevPage / peer_rating_count) * 100, 100)
        }
      ))
    }));
  };

  render() {
    const {
      survey
    } = this.props;

    const {
      show,
      questionsOrder,
      isIntroScreen,
      isFinalScreen,
      isTransitionScreen,
      currentPage,
      progressBar,
      peer_rating_count,
      teamList,
      questionsCount,
      currentUserId,
      submitPages,
      readonlyMode,
    } = this.state;

    const currentQuestion = questionsOrder[currentPage];
    const hasAnswer = true;

    return (
      <ChartButton>
        <ChartButtonContent>
          <h3>
            {ucFirst(survey.stage)} <br /> Feedback
          </h3>
          <span onClick={this.handleShowModal}>
            View Details &gt;
          </span>
        </ChartButtonContent>
        <PeerEvaluationModal
          surveyId={survey.id}
          show={show}
          onHide={this.handleHideModal}
          onSubmit={this.goToNextPage}
          progressBar={progressBar}
          submitButtonText={submitPages[currentPage] ? "Submit" : isIntroScreen ? "Start" : "Save & Continue"}
          currentPage={currentPage ? Number(currentPage) : 0}
          countOfPages={questionsCount ? Number(questionsCount) : 0}
          goNext={this.goToNextPage}
          goPrev={this.goToPrevPage}
          canSubmit={hasAnswer}
          isFinalScreen={isFinalScreen}
          isTransitionScreen={isTransitionScreen}
          readonlyMode={readonlyMode}
          linearExtraId="_peer-review"
        >
          {!!currentQuestion && (
            <PeerEvaluationFeedback
              question={currentQuestion}
              answers={[]}
              peer_rating_count={peer_rating_count}
              teamList={teamList}
              currentUserId={currentUserId}
              feedbacks={{}}
              readonlyMode={readonlyMode}
            />
          )}
        </PeerEvaluationModal>
      </ChartButton>
    );
  }
}

export default ChartViewMore;



// WEBPACK FOOTER //
// ./src/components/peer-review/chart-view-more.js